// src/pages/TripJoin.jsx
// Public join / check-in landing page. The QR points here:
//   Check-in QR:  /trip-join?s=<token>
//   Boarding QR:  /trip-join?s=<token>&c=board
//
// The checkpoint (check-in vs. boarding) is read from the URL and forwarded to
// joinTripSpace, which records a SEPARATE log for each — so a kid who scans the
// bus QR gets a boardedAt stamp without losing their original check-in time.
//
// Auth flow (mobile-safe):
//   - Firebase auth resolves (onAuthStateChanged).
//   - Signed in  -> POST token + checkpoint + ID token, show result.
//   - Signed out -> "Sign in" button -> responsive /login with ?next= back here.
//                   Login uses POPUP sign-in (works on mobile Safari), then
//                   navigates back, where auth is resolved and the scan proceeds.

import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";

const KA_RED = "#E83530";
const KA_NAVY = "#233C5F";
const KA_GOLD = "#A29164";

const JOIN_ENDPOINT =
  process.env.REACT_APP_JOIN_URL ||
  "https://us-central1-kings-app-74847.cloudfunctions.net/joinTripSpace";

function getParams() {
  const p = new URLSearchParams(window.location.search);
  const token = (p.get("s") || "").trim();
  const checkpoint = p.get("c") === "board" ? "board" : "checkin";
  return { token, checkpoint };
}

export default function TripJoin() {
  const navigate = useNavigate();
  const [state, setState] = useState("init"); // init|joining|done|already|closed|signin|error
  const [tripName, setTripName] = useState("");
  const [spaceUri, setSpaceUri] = useState("");
  const [message, setMessage] = useState("");
  const startedRef = useRef(false);

  const { token, checkpoint } = getParams();
  const isBoard = checkpoint === "board";

  useEffect(() => {
    const auth = getAuth();

    if (!token) {
      setMessage("This link is missing its trip code.");
      setState("error");
      return;
    }

    async function doJoin(user) {
      if (startedRef.current) return;
      startedRef.current = true;
      setState("joining");
      try {
        const idToken = await user.getIdToken(true);
        const resp = await fetch(JOIN_ENDPOINT, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({ token, checkpoint }),
        });
        const data = await resp.json().catch(() => ({}));

        if (resp.ok && data.ok) {
          setTripName(data.tripName || "");
          setSpaceUri(data.spaceUri || "");
          // Boarding always confirms as a fresh "done" (the bus log is what
          // matters); check-in distinguishes first-time vs. already a member.
          setState(isBoard ? "done" : data.alreadyMember ? "already" : "done");
          return;
        }
        if (resp.status === 410) {
          setTripName(data.tripName || "");
          setState("closed");
          return;
        }
        if (data.error === "wrong-domain") {
          setMessage("Please sign in with your King's Academy account.");
          setState("error");
          return;
        }
        if (data.error === "unknown-trip") {
          setMessage("This trip link is no longer valid.");
          setState("error");
          return;
        }
        setMessage(data.detail || data.error || "Something went wrong.");
        setState("error");
      } catch (e) {
        setMessage(e.message || "Something went wrong.");
        setState("error");
      }
    }

    // onAuthStateChanged reports a RESOLVED auth state (never the transient
    // null), so we act on the real value: join if signed in, else prompt.
    const unsub = onAuthStateChanged(auth, (user) => {
      if (user) {
        doJoin(user);
      } else {
        setState("signin");
      }
    });
    return () => unsub();
    // eslint-disable-next-line
  }, [token, checkpoint]);

  const goSignIn = () => {
    const q = `s=${encodeURIComponent(token)}${isBoard ? "&c=board" : ""}`;
    const back = `/trip-join?${q}`;
    navigate(`/login?next=${encodeURIComponent(back)}`);
  };

  const accent = isBoard ? KA_RED : KA_NAVY;
  const eyebrow = isBoard ? "KING'S ACADEMY · BUS BOARDING" : "KING'S ACADEMY · TRIP CHECK-IN";

  return (
    <div style={wrap}>
      <div style={{ ...card, borderTop: `5px solid ${accent}` }}>
        <div style={{ color: isBoard ? KA_RED : KA_RED, fontWeight: 700, fontSize: 13, letterSpacing: 1 }}>
          {eyebrow}
        </div>

        {(state === "init" || state === "joining") && (
          <>
            <h2 style={h2}>{isBoard ? "Checking you onto the bus…" : "Checking you in…"}</h2>
            <p style={p}>One moment.</p>
            <Spinner accent={accent} />
          </>
        )}

        {state === "signin" && (
          <>
            <h2 style={h2}>{isBoard ? "Board the bus" : "Join the trip"}</h2>
            <p style={p}>
              Sign in with your King's Academy account to {isBoard ? "confirm you're on the bus." : "check in and join the trip space."}
            </p>
            <button style={{ ...btn, background: accent }} onClick={goSignIn}>
              Sign in with Google
            </button>
          </>
        )}

        {state === "done" && isBoard && (
          <>
            <h2 style={h2}>You're on the bus ✅{tripName ? ` — ${tripName}` : ""}</h2>
            <p style={p}>
              You're checked onto the bus. Keep your phone handy for trip updates
              in the chat. Safe travels!
            </p>
          </>
        )}

        {state === "done" && !isBoard && (
          <>
            <h2 style={h2}>You're checked in ✅{tripName ? ` — ${tripName}` : ""}</h2>
            <p style={p}>
              You've been added to the trip chat. Open the Google Chat app —
              signed in with your KA account — and the space will be there.
            </p>
            {spaceUri && <OpenBtn href={spaceUri} />}
            <Tip />
          </>
        )}

        {state === "already" && (
          <>
            <h2 style={h2}>Already checked in{tripName ? ` — ${tripName}` : ""}</h2>
            <p style={p}>
              You're already in the trip space. Open the Google Chat app with
              your KA account and it will be there.
            </p>
            {spaceUri && <OpenBtn href={spaceUri} />}
            <Tip />
          </>
        )}

        {state === "closed" && (
          <>
            <h2 style={h2}>This trip has closed</h2>
            <p style={p}>
              {tripName ? `"${tripName}" ` : "This trip "} is no longer active.
            </p>
          </>
        )}

        {state === "error" && (
          <>
            <h2 style={h2}>Couldn't {isBoard ? "board" : "check in"}</h2>
            <p style={p}>{message}</p>
            <button style={{ ...btn, background: accent }} onClick={goSignIn}>
              Sign in and try again
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function OpenBtn({ href }) {
  return (
    <a href={href} target="_blank" rel="noreferrer" style={{ ...btn, textDecoration: "none" }}>
      Open in Google Chat
    </a>
  );
}

function Tip() {
  return (
    <p style={{ ...p, fontSize: 12, color: KA_GOLD, marginTop: 20 }}>
      Make sure the Google Chat app is installed and notifications are on, so you
      don't miss trip updates.
    </p>
  );
}

function Spinner({ accent }) {
  return (
    <div
      style={{
        width: 28,
        height: 28,
        border: `3px solid #eee`,
        borderTopColor: accent || KA_RED,
        borderRadius: "50%",
        margin: "18px auto 0",
        animation: "tj-spin 0.8s linear infinite",
      }}
    >
      <style>{`@keyframes tj-spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

const wrap = {
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "#f4f5f7",
  padding: 20,
};
const card = {
  background: "#fff",
  borderRadius: 16,
  padding: "36px 32px",
  maxWidth: 420,
  width: "100%",
  textAlign: "center",
  boxShadow: "0 8px 30px rgba(0,0,0,0.08)",
};
const h2 = { color: KA_NAVY, margin: "16px 0 8px", fontSize: 22 };
const p = { color: "#555", fontSize: 15, lineHeight: 1.5, margin: 0 };
const btn = {
  display: "inline-block",
  marginTop: 20,
  background: KA_RED,
  color: "#fff",
  border: "none",
  borderRadius: 10,
  padding: "12px 22px",
  fontSize: 15,
  cursor: "pointer",
};